import { useCallback, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Check, Sparkles, Crown, Zap, Loader2, Star,
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const planAccent = {
  free: { Icon: Sparkles, color: '#94a3b8' },
  plus: { Icon: Zap, color: '#81d8d0' },
  pro: { Icon: Crown, color: '#f59e0b' },
};

export const BillingPage = () => {
  const { isDark } = useTheme();
  const { token } = useAuth();
  const [plans, setPlans] = useState([]);
  const [me, setMe] = useState(null);
  const [usage, setUsage] = useState(null);
  const [yearly, setYearly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [plansRes, meRes, usageRes] = await Promise.all([
        axios.get(`${API_URL}/api/billing/plans`),
        axios.get(`${API_URL}/api/billing/me`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/billing/usage`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setPlans(plansRes.data.plans);
      setMe(meRes.data);
      setUsage(usageRes.data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleUpgrade = async (planId) => {
    if (planId === 'free') return;
    setProcessing(planId);
    try {
      const checkout = await axios.post(
        `${API_URL}/api/billing/checkout`,
        { plan_id: planId, billing_cycle: yearly ? 'yearly' : 'monthly' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Simulated immediate confirmation (in prod: redirect to Stripe checkout URL)
      const confirmed = await axios.post(
        `${API_URL}/api/billing/confirm?session_id=${checkout.data.session_id}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(confirmed.data.message || 'Abonnement activé');
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du paiement');
    } finally {
      setProcessing(null);
    }
  };

  const handleCancel = async () => {
    setProcessing('cancel');
    try {
      const res = await axios.post(`${API_URL}/api/billing/cancel`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.message);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setProcessing(null);
    }
  };

  const handleReactivate = async () => {
    setProcessing('reactivate');
    try {
      const res = await axios.post(`${API_URL}/api/billing/reactivate`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.message);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0F0F1A] text-white' : 'bg-[#FAFBFC] text-black'}`}>
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const currentPlanId = me?.subscription?.plan_id || 'free';
  const cancelScheduled = me?.subscription?.cancel_at_period_end;

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0F0F1A] text-white' : 'bg-[#FAFBFC] text-black'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-10 backdrop-blur-xl ${isDark ? 'bg-[#0F0F1A]/80 border-white/10' : 'bg-white/80 border-black/10'} border-b`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 hover:opacity-70 transition" data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5" /> <span className="text-sm">Retour</span>
          </Link>
          <h1 className="text-lg font-medium">Abonnement</h1>
          <div className="w-20" />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Mode simulé banner */}
        <div className={`mb-8 px-4 py-3 rounded-xl text-xs ${isDark ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-yellow-50 border-yellow-200'} border`} data-testid="simulated-banner">
          <Star className="w-3.5 h-3.5 inline-block mr-2 -mt-0.5" />
          <strong>Mode démo :</strong> Stripe n'est pas encore connecté. Tu peux essayer l'expérience complète, aucun paiement n'est réellement effectué.
        </div>

        {/* Usage tracker */}
        {usage && (
          <Card className={`mb-10 p-6 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-black/5'}`} data-testid="usage-card">
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="font-medium">Ton utilisation actuelle</h3>
              <span className="text-xs opacity-60">Plan {usage.plan_name}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {Object.entries(usage.items).map(([key, item]) => {
                const labels = {
                  aids_per_month: 'Aides / mois',
                  documents_total: 'Documents',
                  ocr_per_month: 'OCR / mois',
                  letters_per_month: 'Lettres / mois',
                  chat_per_day: 'Chat / jour',
                };
                const reached = !item.unlimited && item.remaining === 0;
                return (
                  <div key={key} data-testid={`usage-${key}`}>
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span className="text-xs opacity-70">{labels[key] || key}</span>
                      <span className={`text-xs font-medium ${reached ? 'text-red-400' : ''}`}>
                        {item.unlimited ? '∞' : `${item.used} / ${item.limit}`}
                      </span>
                    </div>
                    <Progress
                      value={item.unlimited ? 0 : item.percent}
                      className={`h-1.5 ${reached ? 'bg-red-500/20' : ''}`}
                    />
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Title */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <h2 className="text-4xl sm:text-5xl font-light mb-3">Choisis ton plan</h2>
          <p className="opacity-60">Annule à tout moment, sans engagement.</p>

          {/* Toggle monthly/yearly */}
          <div className="flex items-center justify-center gap-3 mt-6">
            <span className={`text-sm ${!yearly ? 'opacity-100' : 'opacity-50'}`}>Mensuel</span>
            <Switch checked={yearly} onCheckedChange={setYearly} data-testid="toggle-yearly" />
            <span className={`text-sm ${yearly ? 'opacity-100' : 'opacity-50'}`}>
              Annuel <span className="text-[10px] uppercase tracking-wider ml-1 text-[#81d8d0]">−18%</span>
            </span>
          </div>
        </motion.div>

        {/* Plans grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((plan) => {
            const isCurrent = plan.id === currentPlanId;
            const accent = planAccent[plan.id] || planAccent.free;
            const Icon = accent.Icon;
            const price = yearly ? plan.price_yearly : plan.price_monthly;
            const cycle = yearly ? '/an' : '/mois';
            return (
              <motion.div
                key={plan.id}
                whileHover={{ y: -4 }}
                data-testid={`plan-${plan.id}`}
              >
                <Card className={`p-6 h-full flex flex-col relative ${plan.is_popular ? 'ring-2 ring-[#81d8d0]' : ''} ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-black/5'}`}>
                  {plan.is_popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest bg-[#81d8d0] text-black font-medium">
                      Populaire
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${accent.color}25` }}>
                      <Icon className="w-5 h-5" style={{ color: accent.color }} />
                    </div>
                    <div>
                      <div className="font-medium">{plan.name}</div>
                      <div className="text-xs opacity-60">{plan.tagline}</div>
                    </div>
                  </div>

                  <div className="mb-5">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-light" data-testid={`plan-${plan.id}-price`}>
                        {price === 0 ? 'Gratuit' : `${price.toFixed(2).replace('.', ',')} €`}
                      </span>
                      {price > 0 && <span className="opacity-50 text-sm">{cycle}</span>}
                    </div>
                  </div>

                  <ul className="space-y-2 mb-6 flex-1">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#81d8d0]" />
                        <span className="opacity-80">{f}</span>
                      </li>
                    ))}
                  </ul>

                  {isCurrent ? (
                    <div className="space-y-2">
                      <Button disabled className="w-full" data-testid={`current-${plan.id}`}>
                        Plan actuel
                      </Button>
                      {plan.id !== 'free' && !cancelScheduled && (
                        <Button variant="ghost" className="w-full text-xs opacity-60" onClick={handleCancel} disabled={processing === 'cancel'} data-testid="cancel-subscription">
                          {processing === 'cancel' ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Résilier'}
                        </Button>
                      )}
                      {plan.id !== 'free' && cancelScheduled && (
                        <Button variant="ghost" className="w-full text-xs text-[#81d8d0]" onClick={handleReactivate} disabled={processing === 'reactivate'} data-testid="reactivate-subscription">
                          {processing === 'reactivate' ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Réactiver (annulation prévue)'}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Button
                      onClick={() => handleUpgrade(plan.id)}
                      disabled={processing === plan.id || plan.id === 'free'}
                      className={`w-full ${plan.id !== 'free' ? 'bg-[#81d8d0] text-black hover:bg-[#6bc4bc]' : ''}`}
                      data-testid={`upgrade-${plan.id}`}
                    >
                      {processing === plan.id ? <Loader2 className="w-4 h-4 animate-spin" /> :
                        plan.id === 'free' ? 'Inclus par défaut' : `Choisir ${plan.name}`}
                    </Button>
                  )}
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
